import { useMemo, useState } from "react";

import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { Modal } from "@/Common/Components/Modals/Modal";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { MedicalSummaryConfigurationHelper } from "@/Patient/Helpers/MedicalSummaryConfigurationHelper";
import { SummaryLayoutItem } from "@/Patient/Models/SummaryLayoutItem";
import { MedicalSummaryViewChangesPopup } from "./MedicalSummaryViewChangesPopup/MedicalSummaryViewChangesPopup";
import { MedicalSummarySections } from "./MedicalSummaryEnum";
import { SummaryArrangement } from "./SummaryArrangement";

interface IMedicalSummaryConfigurationPopupProps {
    onCancel: () => void;
    onApplyToAllPatients: (layout: SummaryLayoutItem[], showProblemsDescription: boolean) => Promise<void>;
    onApplyToCurrentPatient: (layout: SummaryLayoutItem[], showProblemsDescription: boolean, patientId: string) => Promise<void>;
}

function MedicalSummaryConfigurationPopup(props: IMedicalSummaryConfigurationPopupProps): JSX.Element {
    const { getText } = useTranslation();
    const initialGridLayout = MedicalSummaryConfigurationHelper.getSpecificOrDefaultSummaryConfigurationGridLayout().filter(layout => layout.Section !== MedicalSummarySections.VitalSignsSection);
    const [showProblemsDescription, setShowProblemsDescription] = useState<boolean>(MedicalSummaryConfigurationHelper.getProblemsDescription());
    const [gridLayout, setGridLayout] = useState<SummaryLayoutItem[]>(initialGridLayout);
    const [showSummaryViewChangesPopup, setShowSummaryViewChangesPopup] = useState<boolean>(false);

    const disabledSectionLayout = MedicalSummaryConfigurationHelper.getDisabledSummaryConfigurationGridLayout();
    const hiddenSectionLayout = useMemo(() => gridLayout.filter(s => s.IsHidden), [gridLayout]);
    const visibleSectionLayout = useMemo(() => gridLayout.filter(s => !s.IsHidden), [gridLayout]);

    function updateLayout(layout: SummaryLayoutItem[]): void {
        const updatedLayout = [...gridLayout];

        for (const item of layout) {
            const matchingLayout = updatedLayout.firstOrDefault(updatedLayoutItem => updatedLayoutItem.Section === item.Section);

            if (matchingLayout) {
                Object.assign(matchingLayout, item);
            }
        }

        setGridLayout(updatedLayout);
    }

    function updateVisibility(section: MedicalSummarySections): void {
        setGridLayout(prevLayout => prevLayout.map(item => item.Section === section
            ? { ...item, IsHidden: !item.IsHidden }
            : item
        ));
    }

    function onCloseSummaryViewChangesModal(): void {
        setShowSummaryViewChangesPopup(false);
    }

    function onOpenSummaryViewChangesModal(): void {
        setShowSummaryViewChangesPopup(true);
    }

    function renderFooters(): JSX.Element {
        return (
            <div className="p-footer">
                <div className="space-between">
                    <div className="styles.buttons-display flex-container flex-end">
                        <button className="btn btn-default margin-sm-left" onClick={() => props.onCancel()}>
                            {getText("CancelLabel")}
                        </button>
                        <button className="btn btn-primary margin-sm-left" onClick={() => onOpenSummaryViewChangesModal()}>
                            {getText("MedicalSummary_ApplyToAllPatientsLabel")}
                        </button>
                        <button className="btn btn-primary margin-sm-left" onClick={() => props.onApplyToCurrentPatient(gridLayout, showProblemsDescription, patientStore.getCurrentPatientContactId())}>
                            {getText("MedicalSummary_ApplyToThisPatientLabel")}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    function onReset(): void {
        setGridLayout(MedicalSummaryConfigurationHelper.getOriginalSummaryConfigurationGridLayout());
    }

    return (
        <>
            <Modal
                showX
                centerContent
                draggable
                title={getText("ConfigureSummary_TitleLabel")}
                width={900}
                maxHeight="557px"
                top={1}
                showOverlay
                onClose={() => props.onCancel()}
                footer={renderFooters()}
            >
                <div className="margin-md">
                    <CheckboxControl
                        id="show-problems-description-checkbox"
                        label={getText("ConfigureSummary_ShowProblemsDescriptionLabel")}
                        checked={showProblemsDescription}
                        onChange={() => setShowProblemsDescription(!showProblemsDescription)}
                    />
                </div>
                <div className="display-flex-with-space-between margin-md">
                    <p className="text-bold">
                        {getText("ConfigureSummary_InstructionLabel")}
                    </p>
                    <a
                        className="inline-block"
                        onClick={() => onReset()}
                    >
                        {getText("ConfigureSummary_ResetLabel")}
                    </a>
                </div>
                <SummaryArrangement
                    layout={visibleSectionLayout}
                    setLayout={layout => updateLayout(layout)}
                    onVisibilityChange={section => updateVisibility(section)}
                    isDraggable
                />
                <SummaryArrangement
                    layout={disabledSectionLayout}
                    setLayout={layout => updateLayout(layout)}
                    onVisibilityChange={section => updateVisibility(section)}
                    isDraggable={false}
                    isDisabled
                />
                {hiddenSectionLayout.any() && (
                    <div>
                        <div className="margin-md-left margin-md-top+">
                            <p>{getText("ConfigureSummary_HiddenSectionsLabel")}</p>
                        </div>
                        <SummaryArrangement
                            layout={hiddenSectionLayout}
                            setLayout={layout => updateLayout(layout)}
                            onVisibilityChange={section => updateVisibility(section)}
                            isDraggable={false}
                        />
                    </div>
                )}
            </Modal>
            {showSummaryViewChangesPopup && (
                <MedicalSummaryViewChangesPopup
                    onClose={() => onCloseSummaryViewChangesModal()}
                    onContinue={() => props.onApplyToAllPatients(gridLayout, showProblemsDescription)}
                />
            )}
        </>
    );
}

export { MedicalSummaryConfigurationPopup };
